import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import * as userProfileService from '../../../services/userProfileService';
import UserHeader from './components/UserHeader';
import StatsCards from './components/StatsCards';
import ActivityTimeline from './components/ActivityTimeline';
import CallsTab from './components/tabs/CallsTab';
import MemoriesTab from './components/tabs/MemoriesTab';
import PerformanceTab from './components/tabs/PerformanceTab';

// Lazy: three.js (~600 KB) only loads when the Galaxy tab is opened
const GalaxyTab = lazy(() => import('./components/tabs/GalaxyTab'));
import { ResponsiveContainer } from '../../common/ResponsiveLayout';
import { useMobile } from '../../../hooks/useMobile';

const UserProfilePage = ({ userIdOverride, viewMode = 'admin' }) => {
  const { userId: paramUserId } = useParams();
  const userId = userIdOverride ?? paramUserId;
  const navigate = useNavigate();
  const { user: currentUser, userRole } = useAuth();
  const { isMobile } = useMobile(); // Move hook to top
  
  // Main state
  const [profileData, setProfileData] = useState({
    user: null,
    callStats: null,
    memoryStats: null,
    timeline: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Check access permissions
  const hasAccess = useCallback(() => {
    if (!currentUser || !userRole) return false;
    
    // Super admin can view any profile
    if (userRole === 'SUPER_ADMIN') return true;
    
    // Users can view their own profile
    if (currentUser.id === userId) return true;
    
    // Coaches can view their assigned clients (we'll validate this server-side)
    if (userRole === 'COACH') return true;
    
    return false;
  }, [currentUser, userRole, userId]);

  // Fetch user profile data
  const fetchProfileData = useCallback(async () => {
    if (!userId || !hasAccess()) {
      setError('Access denied');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await userProfileService.getUserProfile(userId);
      
      if (result.error) {
        throw new Error(result.error);
      }

      setProfileData(result);
    } catch (err) {
      console.error('Failed to fetch profile data:', err);
      setError(err.message || 'Failed to load user profile');
    } finally {
      setLoading(false);
    }
  }, [userId, hasAccess]);

  // Initial data fetch
  useEffect(() => {
    fetchProfileData();
  }, [fetchProfileData, refreshTrigger]);

  // Refresh data (for child components to trigger)
  const refreshData = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
  }, []);

  // Handle navigation back to user list
  const handleBackToList = () => {
    navigate('/admin');
  };

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  // Handle user data updates (from child components)
  const handleUserUpdate = useCallback((updatedUser) => {
    setProfileData(prev => ({
      ...prev,
      user: { ...prev.user, ...updatedUser }
    }));
  }, []);

  // Access control check
  if (!hasAccess()) {
    return (
      <div className="p-4">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <h3 className="font-bold">Access Denied</h3>
          <p>You don't have permission to view this user profile.</p>
          <button
            onClick={handleBackToList}
            className="mt-2 text-red-800 hover:text-red-900 underline"
          >
            Back to User List
          </button>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="p-4">
        <div className="animate-pulse">
          {/* Header skeleton */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-gray-200 rounded-full"></div>
              <div>
                <div className="h-6 bg-gray-200 rounded w-48 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-32"></div>
              </div>
            </div>
            <div className="h-10 bg-gray-200 rounded w-32"></div>
          </div>
          
          {/* Stats cards skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white p-4 rounded-lg shadow">
                <div className="h-8 bg-gray-200 rounded w-16 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-24"></div>
              </div>
            ))}
          </div>
          
          {/* Tab skeleton */}
          <div className="bg-white rounded-lg shadow">
            <div className="flex border-b">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-12 bg-gray-200 rounded-t w-24 mr-2"></div>
              ))}
            </div>
            <div className="p-6">
              <div className="h-64 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <h3 className="font-bold">Error Loading Profile</h3>
          <p>{error}</p>
          <div className="mt-4 space-x-2">
            <button
              onClick={refreshData}
              className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
            >
              Try Again
            </button>
            <button
              onClick={handleBackToList}
              className="text-red-800 hover:text-red-900 underline"
            >
              Back to User List
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { user, callStats, memoryStats, timeline } = profileData;

  // Tabs configuration
  const allTabs = [
    {
      id: 'overview',
      label: 'Overview',
      icon: '📊',
      component: () => (
        <div className="space-y-6">
          <StatsCards
            callStats={callStats}
            memoryStats={memoryStats}
            onRefresh={refreshData}
            viewMode={viewMode}
          />
          {viewMode !== 'client' && (
            <ActivityTimeline
              activities={timeline}
              userId={userId}
              onRefresh={refreshData}
            />
          )}
        </div>
      )
    },
    {
      id: 'calls',
      label: 'Contacts',
      icon: '📞',
      badge: callStats?.total_calls || 0,
      component: () => (
        <CallsTab
          userId={userId}
          callStats={callStats}
          onRefresh={refreshData}
          viewMode={viewMode}
        />
      )
    },
    {
      id: 'memories',
      label: 'Memories',
      icon: '🧠',
      badge: memoryStats?.total_memories || 0,
      component: () => (
        <MemoriesTab
          userId={userId}
          memoryStats={memoryStats}
          onRefresh={refreshData}
          dashboardMode={viewMode}
        />
      )
    },
    {
      id: 'galaxy',
      label: 'Galaxy',
      icon: '🌌',
      component: () => (
        <Suspense
          fallback={
            <div className="flex h-[70vh] min-h-[420px] items-center justify-center rounded-lg bg-[#05060f]">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-500 border-t-white" />
            </div>
          }
        >
          <GalaxyTab userId={userId} dashboardMode={viewMode} />
        </Suspense>
      )
    },
    {
      id: 'performance',
      label: 'Performance',
      icon: '📈',
      component: () => (
        <PerformanceTab
          userId={userId}
          callStats={callStats}
          memoryStats={memoryStats}
          userRole={userRole}
          onRefresh={refreshData}
        />
      )
    }
  ];

  // Filter tabs based on user role and view mode
  // Coaches: hide Overview; clients: hide Performance
  const tabs = viewMode === 'client'
    ? allTabs.filter(tab => tab.id !== 'performance')
    : userRole === 'COACH'
      ? allTabs.filter(tab => tab.id !== 'overview')
      : allTabs;

  // Ensure activeTab is valid for the current user's available tabs
  const validActiveTab = tabs.find(tab => tab.id === activeTab) ? activeTab : tabs[0]?.id || 'calls';
  
  const activeTabConfig = tabs.find(tab => tab.id === validActiveTab) || tabs[0];

  return (
    <div className="min-h-screen bg-gray-50 overflow-hidden">
      <ResponsiveContainer maxWidth="7xl" padding={{ xs: 0, lg: 4 }}>
        {/* Header */}
        <UserHeader
          user={user}
          currentUser={currentUser}
          userRole={userRole}
          callStats={callStats}
          memoryStats={memoryStats}
          onUserUpdate={handleUserUpdate}
          onBackToList={handleBackToList}
          onRefresh={refreshData}
          viewMode={viewMode}
        />

        {/* Tab Navigation */}
        <div className="bg-white rounded-none sm:rounded-lg shadow-sm sm:shadow mb-4 sm:mb-6 mx-0 sm:mx-4">
          <div className="border-b border-gray-200">
            {/* Mobile: Horizontal scrollable tabs */}
            {isMobile ? (
              <div className="overflow-x-auto scrollbar-hide">
                <nav className="flex min-w-max" aria-label="Tabs">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => handleTabChange(tab.id)}
                      className={`flex-shrink-0 py-4 px-4 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                        validActiveTab === tab.id
                          ? 'border-indigo-500 text-indigo-600'
                          : 'border-transparent text-gray-500'
                      }`}
                    >
                      <span className="text-base">{tab.icon}</span>
                      <span className="whitespace-nowrap">{tab.label}</span>
                      {tab.badge !== undefined && (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          validActiveTab === tab.id
                            ? 'bg-indigo-100 text-indigo-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {tab.badge}
                        </span>
                      )}
                    </button>
                  ))}
                </nav>
              </div>
            ) : (
              /* Desktop: Regular tabs */
              <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                      validActiveTab === tab.id
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <span>{tab.icon}</span>
                    <span>{tab.label}</span>
                    {tab.badge !== undefined && (
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        validActiveTab === tab.id
                          ? 'bg-indigo-100 text-indigo-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {tab.badge}
                      </span>
                    )}
                  </button>
                ))}
              </nav>
            )}
          </div>

          {/* Tab Content */}
          <div className={`${isMobile ? 'p-4' : 'p-6'} overflow-x-auto`}>
            <div className="min-w-0">
              {/* Called, not rendered as <Component />: each arrow is re-created on
                  every render, so as an element type React would unmount and
                  remount the tab (and rebuild the Galaxy's 3D scene) on any
                  page re-render, e.g. a window resize. */}
              {activeTabConfig.component()}
            </div>
          </div>
        </div>
      </ResponsiveContainer>
    </div>
  );
};

export default UserProfilePage;